import { useState } from "react";
import { useSession } from "@/lib/SessionContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { base44 } from "@/api/base44Client";
import {
  Mail, Eye, EyeOff, CreditCard, Phone, Building2,
  Briefcase, CalendarDays, LogOut, Camera, User, Pencil, X, Save, CheckCircle2, ScanFace, MessageSquare, Brain } from
"lucide-react";
import FaceRegistrationModal from "@/components/attendance/FaceRegistrationModal";
import ConnectedDevicesSection from "@/components/profile/ConnectedDevicesSection";
import toast from "react-hot-toast";

const ROLE_BADGE = {
  admin: "bg-red-100 text-red-700",
  supervisor: "bg-purple-100 text-purple-700",
  operator: "bg-blue-100 text-blue-700",
  user: "bg-gray-100 text-gray-600"
};

export default function ProfilSaya() {
  const { member, logout } = useSession();
  const navigate = useNavigate();
  const [showPass, setShowPass] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const { refreshMember } = useSession();

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isFaceModalOpen, setIsFaceModalOpen] = useState(false);
  const [showFacePreview, setShowFacePreview] = useState(false);
  const [editData, setEditData] = useState({ full_name: '', employee_id: '', password: '', phone: '', asa_nickname: '', asa_personality: '', face_descriptor: '' });

  if (!member) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <User className="w-12 h-12 text-muted-foreground opacity-30" />
        <p className="text-muted-foreground text-sm">Anda belum login sebagai anggota tim.</p>
        <Button onClick={() => navigate("/login-tim")}>Login Tim</Button>
      </div>);

  }

  const initials = member.full_name?.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    const { file_url } = await base44.integrations.Core.UploadFile({ file });
    await base44.entities.TeamMember.update(member.id, { photo_url: file_url });
    await refreshMember();
    setUploadingPhoto(false);
  };

  const handleLogout = () => {
    logout();
    navigate("/login-tim");
  };

  const formatDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
  };

  const handleEditClick = () => {
    setEditData({
      full_name: member.full_name || '',
      employee_id: member.employee_id || '',
      password: member.password || '',
      phone: member.phone || '',
      asa_nickname: member.asa_nickname || '',
      asa_personality: member.asa_personality || '',
      face_descriptor: member.face_descriptor || ''
    });
    setIsEditing(true);
  };

  const handleSaveProfile = async () => {
    if (!editData.employee_id || !editData.password || !editData.full_name) {
      toast.error("Nama Lengkap, Employee ID, dan Password tidak boleh kosong!");
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        full_name: editData.full_name,
        employee_id: editData.employee_id,
        password: editData.password,
        phone: editData.phone,
        asa_nickname: editData.asa_nickname,
        asa_personality: editData.asa_personality,
        face_descriptor: editData.face_descriptor
      };
      if (editData.face_photo_url) {
        payload.face_photo_url = editData.face_photo_url;
      }
      await base44.entities.TeamMember.update(member.id, payload);
      await refreshMember();
      setIsEditing(false);
      toast.success("Profil berhasil diperbarui!");
    } catch (error) {
      console.error(error);
      toast.error("Gagal menyimpan profil.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto space-y-4 pb-6 relative">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold">Profil Saya</h1>
          <p className="text-muted-foreground text-sm">Informasi akun dan identitas karyawan</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleEditClick} className="gap-2">
          <Pencil className="w-3.5 h-3.5" /> Edit Profil
        </Button>
      </div>

      {/* Avatar Card */}
      <div className="bg-card border border-border rounded-2xl p-5">
        <div className="flex items-center gap-4">
          <div className="relative shrink-0">
            {member.photo_url ?
            <img src={member.photo_url} alt="" className="w-16 h-16 rounded-2xl object-cover border-2 border-border" /> :

            <div className="w-16 h-16 rounded-2xl bg-accent/20 flex items-center justify-center border-2 border-border">
                <span className="text-xl font-bold text-accent">{initials}</span>
              </div>
            }
            <label className={`absolute -bottom-1 -right-1 w-6 h-6 bg-accent text-accent-foreground rounded-full flex items-center justify-center cursor-pointer shadow hover:opacity-90 ${uploadingPhoto ? 'opacity-50 pointer-events-none' : ''}`}>
              <Camera className="w-3 h-3" />
              <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
            </label>
          </div>
          <div>
            <p className="text-lg font-bold leading-tight">{member.full_name}</p>
            <p className="text-sm text-muted-foreground">{member.position || "—"}</p>
            <div className="flex gap-1.5 mt-1.5">
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize ${ROLE_BADGE[member.role] || ROLE_BADGE.user}`}>
                {member.role}
              </span>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
              member.status === "aktif" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`
              }>
                {member.status === "aktif" ? "Aktif" : "Nonaktif"}
              </span>
            </div>
          </div>
        </div>
        

        
      </div>

      {/* Informasi Akun */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground px-5 py-3 border-b border-border bg-muted/30">
          Informasi Akun
        </p>
        <div className="divide-y divide-border">
          <InfoRow icon={Mail} label="Email Login" value={member.email} />
          <div className="flex items-center gap-3 px-5 py-3.5">
            <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
              <Eye className="w-4 h-4 text-muted-foreground" />
            </div>
            <div className="flex-1">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-medium">Password</p>
              <p className="text-sm font-medium font-mono mt-0.5">{showPass ? member.password : "••••••••"}</p>
            </div>
            <button onClick={() => setShowPass(!showPass)} className="text-muted-foreground hover:text-foreground p-1.5 rounded-md hover:bg-muted transition-colors">
              {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <InfoRow icon={CreditCard} label="Employee ID (QR)" value={member.employee_id || "—"} mono />
        </div>
      </div>

      {/* Data Karyawan */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground px-5 py-3 border-b border-border bg-muted/30">
          Data Karyawan
        </p>
        <div className="divide-y divide-border">
          <InfoRow icon={Phone} label="Nomor Telepon" value={member.phone || "—"} />
          <InfoRow icon={Building2} label="Departemen" value={member.department || "—"} />
          <InfoRow icon={Briefcase} label="Jabatan" value={member.position || "—"} />
          <InfoRow icon={CalendarDays} label="Tanggal Bergabung" value={formatDate(member.join_date)} />
        </div>
        {member.notes &&
        <div className="px-5 py-3 border-t border-border">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-medium mb-0.5">Catatan</p>
            <p className="text-sm text-accent">{member.notes}</p>
          </div>
        }
      </div>

      {/* Data ASA AI & Biometrik */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground px-5 py-3 border-b border-border bg-muted/30">
          Data ASA AI & Biometrik
        </p>
        <div className="divide-y divide-border">
          <InfoRow icon={MessageSquare} label="Nama Panggilan ASA" value={member.asa_nickname || "Belum diatur"} />
          <InfoRow icon={Brain} label="Memori Sifat (Personality)" value={member.asa_personality || "Belum diatur"} />
          
          <div className="flex flex-col gap-2 px-5 py-3.5">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
                <ScanFace className="w-4 h-4 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-medium">Data Face ID</p>
                <div className="mt-0.5">
                  {member.face_descriptor ? (
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex items-center gap-1.5 text-emerald-600 text-xs font-bold bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Terdaftar
                      </div>
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] gap-1 text-slate-500 hover:text-slate-700" onClick={() => setShowFacePreview(true)}>
                        <Eye className="w-3 h-3" /> Preview
                      </Button>
                    </div>
                  ) : (
                    <span className="text-sm font-medium text-muted-foreground">Belum ada</span>
                  )}
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="h-8 text-xs shrink-0" 
                onClick={() => setIsFaceModalOpen(true)}
              >
                {member.face_descriptor ? "Update Wajah" : "Pindai Wajah"}
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground italic ml-11">
              *Klik tombol di atas untuk membuka pemindai kamera (menghemat beban memori perangkat).
            </p>
          </div>
        </div>
      </div>

      {/* Perangkat yang Terhubung */}
      <ConnectedDevicesSection member={member} onUpdate={refreshMember} />

      {/* Logout */}
      <Button
        variant="outline"
        className="w-full gap-2 border-destructive/30 text-destructive hover:bg-destructive/10"
        onClick={handleLogout}>
        
        <LogOut className="w-4 h-4" />
        Keluar dari Akun Tim
      </Button>

      {/* Face Preview Modal */}
      {showFacePreview && (
        <div className="fixed inset-0 z-[60] bg-black/80  flex items-center justify-center p-4" onClick={() => setShowFacePreview(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-border flex justify-between items-center bg-muted/20">
              <h3 className="font-bold text-sm text-slate-800">Preview Data Face ID</h3>
              <button onClick={() => setShowFacePreview(false)} className="text-slate-500 hover:bg-slate-200 rounded-full p-1">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 flex flex-col items-center justify-center bg-slate-50">
              {member.face_photo_url ? (
                <img src={member.face_photo_url} alt="Face ID Data" className="w-full max-w-[240px] rounded-xl object-cover border-2 border-slate-200 shadow-sm" />
              ) : (
                <div className="w-full h-32 flex flex-col items-center justify-center text-slate-400 gap-2">
                  <ScanFace className="w-10 h-10 opacity-50" />
                  <p className="text-xs">Foto Face ID tidak tersedia</p>
                </div>
              )}
              <p className="text-[10px] text-muted-foreground mt-4 text-center px-4 leading-relaxed">
                Ini adalah acuan data biometrik wajah Anda yang digunakan sistem untuk absensi kehadiran. Foto profil Anda dapat diubah bebas tanpa mempengaruhi data presensi ini.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      {isEditing && (
        <div className="fixed inset-0 z-50 bg-black/60  flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="px-6 py-4 border-b border-border flex justify-between items-center bg-muted/20">
              <div>
                <h3 className="font-bold text-lg text-slate-800">Edit Profil</h3>
                <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-bold">Data Pribadi & Login</p>
              </div>
              <button 
                onClick={() => setIsEditing(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-200 text-slate-500 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-5">
              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider ml-1">Nama Lengkap (Profil)</label>
                <input 
                  type="text"
                  value={editData.full_name}
                  onChange={e => setEditData({...editData, full_name: e.target.value})}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all"
                  placeholder="Masukkan nama lengkap..."
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider ml-1">Employee ID (QR)</label>
                <input 
                  type="text"
                  value={editData.employee_id}
                  onChange={e => setEditData({...editData, employee_id: e.target.value})}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all"
                  placeholder="Masukkan Employee ID..."
                />
              </div>
              
              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider ml-1">Password</label>
                <div className="relative">
                  <input 
                    type={showPass ? "text" : "password"}
                    value={editData.password}
                    onChange={e => setEditData({...editData, password: e.target.value})}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 pr-12 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all"
                    placeholder="Masukkan password..."
                  />
                  <button 
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
                  >
                    {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider ml-1">Nomor Telepon</label>
                <input 
                  type="text"
                  value={editData.phone}
                  onChange={e => setEditData({...editData, phone: e.target.value})}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all"
                  placeholder="Contoh: 08123456789"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider ml-1">Face ID (Absensi Wajah)</label>
                {editData.face_descriptor ? (
                  <div className="flex items-center gap-2 text-emerald-600 text-xs font-bold bg-emerald-50 px-3 py-3 rounded-xl border border-emerald-100">
                    <CheckCircle2 className="w-4 h-4" /> Wajah Terekam
                    <Button variant="link" className="text-xs h-auto p-0 ml-auto text-indigo-600" onClick={() => setIsFaceModalOpen(true)}>Update Wajah</Button>
                  </div>
                ) : (
                  <Button variant="outline" size="sm" className="h-10 text-xs flex gap-2 w-full border-dashed border-indigo-200 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50" onClick={() => setIsFaceModalOpen(true)}>
                    <Camera className="w-4 h-4" /> Pindai Wajah Baru
                  </Button>
                )}
              </div>

              <div className="pt-2 pb-1 border-t border-muted/50 mt-2">
                <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2 mb-2 ml-1">
                  ASA AI Personality Data
                </h4>
                <div className="grid grid-cols-2 gap-3 bg-slate-50 p-3 rounded-xl border border-slate-100">
                  <div className="space-y-1 col-span-2 md:col-span-1">
                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider ml-1">Nama Panggilan ASA</label>
                    <input 
                      type="text"
                      value={editData.asa_nickname}
                      onChange={e => setEditData({...editData, asa_nickname: e.target.value})}
                      className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all"
                      placeholder="Cth: Budi"
                    />
                  </div>
                  <div className="space-y-1 col-span-2">
                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider ml-1">Memori Sifat & Preferensi (Personality)</label>
                    <textarea 
                      value={editData.asa_personality}
                      onChange={e => setEditData({...editData, asa_personality: e.target.value})}
                      className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all"
                      rows={3}
                      placeholder="Cth: Suka to the point, tegas, tidak suka banyak basa basi."
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-border bg-muted/10 flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setIsEditing(false)}>
                Batal
              </Button>
              <Button 
                onClick={handleSaveProfile} 
                disabled={isSaving}
                className="bg-[#302621] hover:bg-[#302621]/90 text-white shadow-sm gap-2"
              >
                {isSaving ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {isSaving ? "Menyimpan..." : "Simpan Perubahan"}
              </Button>
            </div>
          </div>
        </div>
      )}
      <FaceRegistrationModal 
        isOpen={isFaceModalOpen}
        onClose={() => setIsFaceModalOpen(false)}
        onRegistered={async (descriptorString, base64Image) => {
          let file_url = null;
          if (base64Image) {
            try {
              let arr = base64Image.split(','), mime = arr[0].match(/:(.*?);/)[1],
                  bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n);
              while(n--){ u8arr[n] = bstr.charCodeAt(n); }
              const file = new File([u8arr], `face_acuan_${member.id}.jpg`, {type:mime});
              const res = await base44.integrations.Core.UploadFile({ file });
              file_url = res.file_url;
            } catch(e) {
              console.error("Gagal upload foto acuan", e);
            }
          }

          if (isEditing) {
            const updates = { face_descriptor: descriptorString };
            if (file_url) updates.face_photo_url = file_url;
            setEditData({...editData, ...updates});
          } else {
            try {
              const updates = { face_descriptor: descriptorString };
              if (file_url) updates.face_photo_url = file_url;
              await base44.entities.TeamMember.update(member.id, updates);
              await refreshMember();
              toast.success("Data Face ID berhasil diperbarui!");
            } catch (error) {
              console.error(error);
              toast.error("Gagal memperbarui Face ID");
            }
          }
          setIsFaceModalOpen(false);
        }}
      />
    </div>);

}

function InfoRow({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-center gap-3 px-5 py-3.5">
      <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-muted-foreground" />
      </div>
      <div>
        <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-medium">{label}</p>
        <p className={`text-sm font-medium mt-0.5 ${mono ? 'font-mono text-accent' : ''}`}>{value}</p>
      </div>
    </div>);

}